import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Textarea from 'react-textarea-autosize';
import isEqual from 'lodash/isEqual';
import { GraduationCap } from 'lucide-react';

import EntryDialog from 'components/Portfolio/EntryDialog/EntryDialog';
import {
  CharCount,
  Field,
  YearSelect,
  inputClass,
} from 'components/Portfolio/EntryDialog/EntryFields';
import fieldStyles from 'components/Portfolio/EntryDialog/EntryDialog.module.scss';
import { fromYear, toYear } from 'helpers/careerEntries';
import { getErrorMessage } from 'helpers/apiErrors';

const MAX_DESCRIPTION = 3000;

// Certifications share user.education with Education. They are told apart
// by having no organisation, so this form has no "Issued by" field yet.
const toDraft = (qualification) => ({
  name: qualification?.qualification || '',
  year: toYear(qualification?.ended_at),
  content: qualification?.content || '',
});

const QualificationDialog = ({
  show,
  qualification,
  saving,
  onSave,
  onDelete,
  onClose,
  onAddEducation,
}) => {
  const [initial] = useState(() => toDraft(qualification));
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState('');
  const isEdit = !!qualification;

  const set = (changes) => setDraft((current) => ({ ...current, ...changes }));

  const dirty = !isEqual(draft, initial);
  const canSave = dirty && !!draft.name.trim() && draft.year !== '';

  const handleSave = () => {
    setError('');
    onSave({
      ...qualification,
      qualification: draft.name.trim(),
      content: draft.content,
      // One year is shown, so the start moves with it.
      started_at: fromYear(draft.year, qualification?.started_at),
      ended_at: fromYear(draft.year, qualification?.ended_at),
    }).then(onClose, (apiError) => setError(getErrorMessage(apiError)));
  };

  return (
    <EntryDialog
      show={show}
      title={
        isEdit ? 'Edit certification or award' : 'Add a certification or award'
      }
      subtitle="Licenses, industry certifications and awards you’ve won."
      dirty={dirty}
      canSave={canSave}
      saving={saving}
      saveLabel={isEdit ? 'Save changes' : 'Add'}
      error={error}
      onSave={handleSave}
      onClose={onClose}
      onDelete={isEdit ? onDelete : null}
      deleteLabel="Delete"
      deletePrompt={`Delete ${
        qualification?.qualification || 'this entry'
      }? You can undo straight after.`}
      crossLink={
        onAddEducation && {
          icon: <GraduationCap size={18} strokeWidth={2} aria-hidden="true" />,
          prompt: 'A degree or a course you studied?',
          label: 'Add it to Education',
          onClick: onAddEducation,
        }
      }
    >
      <div className={fieldStyles.row}>
        <Field id="qualification-name" label="Name">
          <input
            id="qualification-name"
            type="text"
            className={inputClass()}
            value={draft.name}
            onChange={(event) => set({ name: event.target.value })}
            placeholder="For example, Adobe Certified Professional"
            maxLength={255}
          />
        </Field>
        <Field id="qualification-year" label="Year">
          <YearSelect
            id="qualification-year"
            value={draft.year}
            onChange={(year) => set({ year })}
            ahead={10}
            placeholder="Select"
          />
        </Field>
      </div>
      <Field
        id="qualification-description"
        label="Description"
        optional
        aside={<CharCount value={draft.content} max={MAX_DESCRIPTION} />}
      >
        <Textarea
          id="qualification-description"
          className={inputClass()}
          value={draft.content}
          onChange={(event) => set({ content: event.target.value })}
          placeholder="Who issued it, what it covers, or the work that won it."
          minRows={3}
          maxRows={12}
          maxLength={MAX_DESCRIPTION}
        />
      </Field>
    </EntryDialog>
  );
};

QualificationDialog.propTypes = {
  show: PropTypes.bool,
  qualification: PropTypes.object,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onAddEducation: PropTypes.func,
};

export default QualificationDialog;
